import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { wabaApi, WABA_BASE } from "../../lib/wabaApi";
import { DialogPanel } from "../shared/DialogPanel";
import { XIcon } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";

interface CTWA { ctwa_clid: string; received_at: string; payload: unknown; }
interface LeadEvent { type: string; received_at: string; body: unknown; }
interface Lead {
  id: string;
  phone: string | null;            // identidade do WhatsApp; null em IG/Messenger/ML/OLX
  bsuid: string | null;
  lid?: string | null;             // LID do WhatsApp (conexão externa); aparece só sem telefone
  channel?: string;                // whatsapp | instagram | messenger | mercadolivre | olx
  meli_user_id?: string | null;
  contact_phone?: string | null;   // telefone cadastrado no ML/OLX (não é identidade)
  email?: string | null;
  document_type?: string | null;
  document_number?: string | null;
  olx_account_id?: string | null;  // publicAccountId do comprador na OLX
  profile_name: string | null;
  username: string | null;
  ctwa_clid: string | null;
  first_seen_at: string;
  last_seen_at: string;
  last_interaction_at: string | null;
  ctwa_history: CTWA[];
  events: LeadEvent[];
}

interface Props { connectionId: string; instanceId: string; onClose: () => void; external?: boolean; }

// A API aceita telefone, bsuid, ctwa_clid ou username no mesmo parâmetro `q`.
function lookupParam(term: string): string {
  const v = term.trim();
  return `q=${v ? encodeURIComponent(v) : "VALOR"}`;
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <span style={{ color: "var(--ink-muted)", minWidth: 64 }}>{label}:</span>
      <span style={{ color: value ? "var(--ink-secondary)" : "var(--line-max)", wordBreak: "break-all" }}>{value || "—"}</span>
    </div>
  );
}

function formatTime(iso: string | null) {
  if (!iso) return "—";
  // Naive = UTC (apenda Z); com offset (Z, +HH:MM ou -HH:MM) usa como veio.
  const hasOffset = iso.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(iso);
  const utc = hasOffset ? iso : iso + "Z";
  return new Date(utc).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
}

export function LeadSearch({ connectionId, instanceId, onClose, external }: Props) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showCurl, setShowCurl] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["waba-leads", connectionId, q],
    queryFn: () => wabaApi.get<Lead[]>(`/connections/${connectionId}/leads/search?q=${encodeURIComponent(q)}`),
  });

  const curl = `curl -s "${WABA_BASE}/leads/lookup?${lookupParam(q)}" \\\n  -H "x-instance-id: ${instanceId}" \\\n  -H "x-api-key: ${apiKey ?? "<sua-api-key>"}"`;

  async function toggleCurl() {
    const next = !showCurl;
    setShowCurl(next);
    if (next && apiKey === null) {
      try {
        const d = await wabaApi.get<{ api_key: string }>(`/connections/${connectionId}/api-key`);
        setApiKey(d.api_key);
      } catch { /* mantém o placeholder <sua-api-key> */ }
    }
  }

  function copyCurl() {
    navigator.clipboard.writeText(curl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}
      {...backdropClose(() => { onClose(); })}>
      <DialogPanel onClose={() => onClose()} label="Buscar leads" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 640, maxHeight: "85vh", display: "flex", flexDirection: "column", gap: 14 }}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>Buscar leads</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={toggleCurl} style={{ background: showCurl ? "var(--line)" : "transparent", border: "1px solid var(--line-strong)", borderRadius: 6, color: "var(--ink-secondary)", fontSize: 11, padding: "4px 9px", cursor: "pointer", fontFamily: "var(--font-mono)" }}>
              {"</> cURL"}
            </button>
            <button aria-label="Fechar" onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--ink-muted)", fontSize: 18, cursor: "pointer" }}><XIcon size={16} /></button>
          </div>
        </div>

        {/* cURL para consumo em outros sistemas (auth = instance_id + api_key da conexão) */}
        {showCurl && (
          <div style={{ background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 8, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>GET /leads/lookup — busca a entidade por telefone, bsuid, ctwa_clid ou username</span>
              <button onClick={copyCurl} style={{ background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 5, color: copied ? "var(--ok)" : "var(--ink-secondary)", fontSize: 11, padding: "3px 9px", cursor: "pointer" }}>
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
            <pre style={{ margin: 0, fontSize: 11, color: "var(--ok)", whiteSpace: "pre-wrap", wordBreak: "break-all", fontFamily: "var(--font-mono)", lineHeight: 1.5 }}>{curl}</pre>
            <span style={{ fontSize: 10, color: "var(--ink-muted)" }}>
              O parâmetro <code style={{ color: "var(--violet)" }}>q</code> aceita telefone, bsuid, ctwa_clid ou username — qualquer um. Resposta inclui o histórico de cliques (ctwa_history).
            </span>
          </div>
        )}

        <input
          autoFocus
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Telefone, bsuid, ctwa_clid ou username..."
          style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", background: "var(--surface-raised)", border: "1px solid var(--line-strong)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
        />

        <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
          {isLoading && <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando...</span>}
          {leads.map(lead => (
            <div key={lead.id} style={{ background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6 }}>
              <div
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", cursor: "pointer" }}
                onClick={() => setExpanded(expanded === lead.id ? null : lead.id)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: "var(--ink)", fontWeight: 600 }}>
                    {lead.profile_name ? `${lead.profile_name} · ` : ""}{lead.phone ?? lead.lid ?? lead.contact_phone ?? ""}{lead.username ? ` · @${lead.username}` : ""}
                    {lead.channel && lead.channel !== "whatsapp" && (
                      <span style={{ fontSize: 10, fontWeight: 600, marginLeft: 6, color: "var(--ink-muted)" }}>{lead.channel}</span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 99, background: "var(--surface-raised)", color: lead.ctwa_history.length ? "var(--violet)" : "var(--line-max)" }}>
                    {lead.ctwa_history.length} ctwa
                  </span>
                  {(lead.events ?? []).filter(e => e.type !== "ctwa").length > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 99, background: "var(--surface-raised)", color: "var(--info)", marginLeft: 4 }}>
                      {lead.events.filter(e => e.type !== "ctwa").length} evento{lead.events.filter(e => e.type !== "ctwa").length > 1 ? "s" : ""}
                    </span>
                  )}
                  <div style={{ fontSize: 10, color: "var(--line-max)", marginTop: 3 }}>últ. interação {formatTime(lead.last_interaction_at)}</div>
                </div>
              </div>
              {expanded === lead.id && (
                <div style={{ padding: "0 12px 12px", borderTop: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 11, color: "var(--ink-muted)", marginTop: 8 }}>
                    1º contato {formatTime(lead.first_seen_at)} · último inbound {formatTime(lead.last_seen_at)}
                  </div>
                  <div style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 3 }}>
                    <Field label="bsuid" value={lead.bsuid} />
                    {external && <Field label="lid" value={lead.lid ?? null} />}{/* só a conexão externa tem lid */}
                    <Field label="telefone" value={lead.phone} />
                    <Field label="ctwa" value={lead.ctwa_clid} />
                    <Field label="username" value={lead.username} />
                    <Field label="canal" value={lead.channel ?? null} />
                    <Field label="meli_user_id" value={lead.meli_user_id ?? null} />
                    <Field label="telefone (canal)" value={lead.contact_phone ?? null} />
                    <Field label="e-mail" value={lead.email ?? null} />
                    <Field label="documento" value={lead.document_number ? `${lead.document_type ?? ""} ${lead.document_number}`.trim() : null} />
                    <Field label="conta OLX" value={lead.olx_account_id ?? null} />
                  </div>
                  {lead.ctwa_history.length === 0 && (
                    <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nenhum clique de anúncio (ctwa) registrado.</span>
                  )}
                  {/* Timeline unificada (events já traz os ctwa intercalados por horário) */}
                  {(lead.events ?? []).map((ev, i) => {
                    const isCtwa = ev.type === "ctwa";
                    const b = ev.body as { ctwa_clid?: string; payload?: unknown } | null;
                    const detail = isCtwa ? b?.payload : ev.body;
                    return (
                      <div key={i} style={{ background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 6, padding: "8px 10px" }}>
                        {isCtwa ? (
                          <div style={{ fontSize: 11, color: "var(--violet)", wordBreak: "break-all" }}><strong>ctwa</strong> · {b?.ctwa_clid}</div>
                        ) : (
                          <div style={{ fontSize: 11, color: "var(--info)", fontWeight: 600 }}>{ev.type}</div>
                        )}
                        <div style={{ fontSize: 10, color: "var(--ink-muted)", margin: "2px 0 6px" }}>{formatTime(ev.received_at)}</div>
                        {detail != null && (
                          <pre style={{ margin: 0, fontSize: 10, color: "var(--ink-soft)", whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 160, overflow: "auto" }}>
                            {JSON.stringify(detail, null, 2)}
                          </pre>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
          {!isLoading && leads.length === 0 && (
            <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhum lead encontrado.</span>
          )}
        </div>
      </DialogPanel>
    </div>
  );
}
